import React, { useCallback, useMemo, useState } from 'react';
import { Crosshair, LayoutGrid, Plus, Sparkles } from 'lucide-react';
import InlineChat from '../InlineChat';
import { ArkorbitGridBackground } from '../arkorbit-ui/ArkorbitGridBackground';
import { PortalBadge } from '../arkorbit-ui/PortalBadge';
import { PortalPixelBubble } from '../arkorbit-ui/PortalPixelBubble';
import { N8N_CONFIG } from '../../constants';
import { UI_ACCENT } from '../arkorbit-ui/portalTheme';
import { WorkspaceConnections } from './WorkspaceConnections';
import { WorkspaceWindow } from './WorkspaceWindow';
import {
  DONNA_WORLD,
  WORKSPACE_PRESETS,
  WorkspaceWindowModel,
} from './types';
import { useCanvasPanZoom } from './useCanvasPanZoom';

let windowIdSeq = 0;
function nextWindowId() {
  windowIdSeq += 1;
  return `ws-${windowIdSeq}`;
}

function spawnPreset(key: keyof typeof WORKSPACE_PRESETS, existing: WorkspaceWindowModel[]) {
  const preset = WORKSPACE_PRESETS[key];
  if (existing.some((w) => w.payload === preset.payload && w.kind === preset.kind)) return existing;
  const win: WorkspaceWindowModel = {
    id: nextWindowId(),
    title: preset.title,
    kind: preset.kind,
    payload: preset.payload,
    width: preset.width,
    height: preset.height,
    x: DONNA_WORLD.hubX + preset.offsetX - preset.width / 2,
    y: DONNA_WORLD.hubY + preset.offsetY - preset.height / 2,
    accent: preset.accent,
  };
  return [...existing, win];
}

const PanelPlaceholder: React.FC<{ title: string; hint: string }> = ({ title, hint }) => (
  <div className="h-full flex flex-col items-center justify-center p-6 text-center bg-slate-950/40">
    <LayoutGrid className="w-8 h-8 text-cyan-400/60 mb-3" />
    <p className="text-white font-semibold text-sm mb-1">{title}</p>
    <p className="text-slate-500 text-xs max-w-[240px] leading-relaxed">{hint}</p>
  </div>
);

/** Donna — Endlos-Canvas (Parität Health-Map `donna_canvas.js`).
 * Hub: größere Bubble (Schritt Richtung HUB_DIM 480px); Chat nur als schwebendes Fenster
 * (Preset `chat`), nicht embedded im Dashboard. Offen: Topbar-Chips + `DonnaWorkspaceTools`
 * in React — DONNA_TOOL_IDEEN_BACKLOG.md */
const DONNA_HUB_BUBBLE_PX = 160;
function initialWindows(): WorkspaceWindowModel[] {
  const preset = WORKSPACE_PRESETS.chat;
  return [{
    id: 'chat-main',
    title: preset.title,
    kind: preset.kind,
    payload: preset.payload,
    width: preset.width,
    height: preset.height,
    x: DONNA_WORLD.hubX + preset.offsetX - preset.width / 2,
    y: DONNA_WORLD.hubY + preset.offsetY - preset.height / 2,
    accent: preset.accent,
  }];
}

export const DonnaWorkspace: React.FC = () => {
  const [busy, setBusy] = useState(false);
  const [windows, setWindows] = useState<WorkspaceWindowModel[]>(initialWindows);
  const [minimized, setMinimized] = useState<Record<string, boolean>>({});
  const [topId, setTopId] = useState<string | null>(null);

  const {
    containerRef,
    pan,
    zoom,
    centerOnFocus,
    onWheel,
    onPointerDownBackground,
    onPointerMoveBackground,
    onPointerUpBackground,
  } = useCanvasPanZoom({
    worldWidth: DONNA_WORLD.width,
    worldHeight: DONNA_WORLD.height,
    focusX: DONNA_WORLD.hubX,
    focusY: DONNA_WORLD.hubY,
  });

  const openPreset = useCallback((key: keyof typeof WORKSPACE_PRESETS) => {
    setWindows((prev) => {
      const next = spawnPreset(key, prev);
      const added = next[next.length - 1];
      if (added && next.length > prev.length) setTopId(added.id);
      return next;
    });
  }, []);

  const closeWindow = useCallback((id: string) => {
    setWindows((prev) => prev.filter((w) => w.id !== id));
    setMinimized((prev) => {
      const n = { ...prev };
      delete n[id];
      return n;
    });
  }, []);

  const updateWindow = useCallback((id: string, patch: Partial<WorkspaceWindowModel>) => {
    setWindows((prev) => prev.map((w) => (w.id === id ? { ...w, ...patch } : w)));
  }, []);

  const zFor = useMemo(() => {
    const map: Record<string, number> = {};
    windows.forEach((w, i) => {
      map[w.id] = 20 + i + (w.id === topId ? 50 : 0);
    });
    return map;
  }, [windows, topId]);

  const renderWindowBody = (w: WorkspaceWindowModel) => {
    if (w.kind === 'iframe' && w.payload) {
      return (
        <iframe
          title={w.title}
          src={w.payload}
          className="w-full h-full border-0 bg-slate-950"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        />
      );
    }
    if (w.payload === 'donna-chat') {
      return (
        <InlineChat
          title="Donna"
          subtitle="ARKORBIT CEO · Canvas"
          initialMessage="Hallo Andreas. Chat-Fenster verschieben & skalieren — Donna bleibt in der Mitte. Was soll ich öffnen?"
          webhookUrl={N8N_CONFIG.INTERNAL_ASSISTANT_WEBHOOK}
          accentColor="purple"
          mode="internal_dashboard"
          timeoutMs={120000}
          hideHeader
          onLoadingChange={setBusy}
          shellClassName="border-0 shadow-none rounded-none bg-transparent min-h-[320px] h-full flex-1"
        />
      );
    }
    if (w.payload === 'pipeline') {
      return (
        <PanelPlaceholder
          title="Pipeline"
          hint="Phase 2: Mandanten-Pipeline als eingebettetes Panel. Sag Donna „öffne Pipeline“ — oder nutze die Sidebar."
        />
      );
    }
    if (w.payload === 'brain') {
      return (
        <PanelPlaceholder
          title="BRAIN"
          hint="Phase 2: BRAIN-Upload und Collections hier andocken."
        />
      );
    }
    return <PanelPlaceholder title={w.title} hint="Inhalt folgt." />;
  };

  return (
    <div className="absolute inset-0 flex flex-col bg-[#080c12]">
      {/* Toolbar */}
      <div className="shrink-0 z-30 flex flex-wrap items-center gap-2 px-4 py-2 border-b border-white/[0.06] bg-slate-950/90 backdrop-blur-md">
        <Sparkles className="w-4 h-4 text-cyan-400" />
        <span className="text-sm font-bold text-white mr-2">Donna Workspace</span>
        <PortalBadge label="Endlos-Canvas" accent="cyan" />
        <div className="flex flex-wrap gap-1.5 ml-auto">
          {(
            [
              ['chat', 'Chat'],
              ['healthMap', 'Health Map'],
              ['workflows', 'n8n'],
              ['brain', 'BRAIN'],
              ['pipeline', 'Pipeline'],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => openPreset(key)}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold text-cyan-300/90 border border-cyan-500/20 bg-cyan-500/5 hover:bg-cyan-500/10 transition-colors"
            >
              <Plus className="w-3 h-3" /> {label}
            </button>
          ))}
          <button
            type="button"
            onClick={centerOnFocus}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold text-slate-400 border border-white/10 hover:text-white hover:bg-white/5"
          >
            <Crosshair className="w-3 h-3" /> Donna zentrieren
          </button>
        </div>
      </div>

      {/* Canvas viewport */}
      <div
        ref={containerRef}
        className="flex-1 min-h-0 overflow-hidden cursor-grab active:cursor-grabbing donna-canvas-viewport"
        onWheel={onWheel}
        onPointerDown={onPointerDownBackground}
        onPointerMove={onPointerMoveBackground}
        onPointerUp={onPointerUpBackground}
        onPointerCancel={onPointerUpBackground}
      >
        <div
          className="absolute top-0 left-0 origin-top-left will-change-transform"
          style={{
            width: DONNA_WORLD.width,
            height: DONNA_WORLD.height,
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          }}
        >
          <ArkorbitGridBackground opacity={0.06} className="!absolute inset-0" />
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage: 'radial-gradient(circle at center, #22d3ee 0%, transparent 55%)',
              backgroundSize: `${DONNA_WORLD.width}px ${DONNA_WORLD.height}px`,
              backgroundPosition: `${DONNA_WORLD.hubX}px ${DONNA_WORLD.hubY}px`,
            }}
          />

          <WorkspaceConnections windows={windows} />

          {/* Donna Hub — nur Bubble, zentriert */}
          <div
            data-donna-hub
            className="absolute flex flex-col items-center pointer-events-none"
            style={{
              left: DONNA_WORLD.hubX - DONNA_HUB_BUBBLE_PX / 2 - 24,
              top: DONNA_WORLD.hubY - DONNA_HUB_BUBBLE_PX / 2 - 48,
              width: DONNA_HUB_BUBBLE_PX + 48,
            }}
          >
            <div className="relative mb-2 pointer-events-auto donna-hub-ring">
              <div
                className="absolute rounded-full border border-cyan-400/20 donna-hub-ring"
                style={{
                  width: DONNA_HUB_BUBBLE_PX + 56,
                  height: DONNA_HUB_BUBBLE_PX + 56,
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
                aria-hidden
              />
              <div className="absolute inset-0 rounded-full bg-cyan-500/10 blur-2xl scale-150" />
              <PortalPixelBubble
                size={DONNA_HUB_BUBBLE_PX}
                active={busy}
                interactive
                color={UI_ACCENT.cyan.pixel}
                className="relative shadow-[0_0_72px_rgba(34,211,238,0.3)]"
              />
            </div>
            <div className="text-center pointer-events-none">
              <h2 className="text-xl font-bold text-white tracking-tight">Donna</h2>
              <div className="flex flex-wrap justify-center gap-1.5 mt-1.5">
                <PortalBadge label="CEO-Assistentin" accent="cyan" effect={busy} />
                <PortalBadge label="Labor" accent="blue" />
              </div>
            </div>
          </div>

          {/* Floating windows */}
          {windows.map((w) => (
            <WorkspaceWindow
              key={w.id}
              model={w}
              zIndex={zFor[w.id] ?? 20}
              minimized={!!minimized[w.id]}
              onFocus={() => setTopId(w.id)}
              onClose={() => closeWindow(w.id)}
              onToggleMinimize={() =>
                setMinimized((prev) => ({ ...prev, [w.id]: !prev[w.id] }))
              }
              onMove={(x, y) => updateWindow(w.id, { x, y })}
              onResize={(width, height) => updateWindow(w.id, { width, height })}
            >
              {renderWindowBody(w)}
            </WorkspaceWindow>
          ))}

        </div>
      </div>
    </div>
  );
};

export default DonnaWorkspace;
